import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../../stores/authStore';
import { usersAPI } from '../../services/api';

const ROLE_META = {
  admin:   { label: 'Admin',        bg: 'bg-purple-100', text: 'text-purple-700' },
  finance: { label: 'Finance',      bg: 'bg-blue-100',   text: 'text-blue-700'   },
  board:   { label: 'Board Member', bg: 'bg-violet-100', text: 'text-violet-700' },
  member:  { label: 'Member',       bg: 'bg-slate-100',  text: 'text-slate-600'  },
};

function RoleBadge({ role }) {
  const m = ROLE_META[role] || ROLE_META.member;
  return (
    <span className={`inline-block rounded-full px-2.5 py-0.5 text-xs font-semibold ${m.bg} ${m.text}`}>
      {m.label}
    </span>
  );
}

const TABS = [
  { key: '',        label: 'All Staff'      },
  { key: 'board',   label: 'Board / Approvers' },
  { key: 'finance', label: 'Finance'        },
  { key: 'admin',   label: 'Admin'          },
];

export default function StaffManagement() {
  const [users, setUsers]           = useState([]);
  const [loading, setLoading]       = useState(true);
  const [activeTab, setActiveTab]   = useState('');
  const [showAdd, setShowAdd]       = useState(false);
  const [editUser, setEditUser]     = useState(null);
  const [tempPassword, setTempPassword] = useState('');
  const [busy, setBusy]             = useState('');
  const [error, setError]           = useState('');
  const navigate = useNavigate();
  const isAdmin = useAuthStore((state) => state.user?.role === 'admin');

  function load() {
    setLoading(true);
    usersAPI.list(activeTab)
      .then(res => setUsers(res.users || []))
      .catch(() => setError('Failed to load staff'))
      .finally(() => setLoading(false));
  }

  useEffect(() => { load(); }, [activeTab]);

  async function handleDelete(id, name) {
    if (!confirm(`Deactivate ${name}?`)) return;
    setBusy(`del_${id}`); setError('');
    try {
      await usersAPI.delete(id);
      load();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed');
    } finally {
      setBusy('');
    }
  }

  async function handleResetPassword(id) {
    setBusy(`pwd_${id}`); setError('');
    try {
      const res = await usersAPI.resetPassword(id);
      alert(`New temporary password: ${res.temp_password}\n\nShare this with the user securely.`);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed');
    } finally {
      setBusy('');
    }
  }

  async function handleUnlock(id) {
    setBusy(`unlock_${id}`); setError('');
    try {
      await usersAPI.unlock(id);
      load();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed');
    } finally {
      setBusy('');
    }
  }

  async function handleEditSave(id, data) {
    setBusy(`edit_${id}`); setError('');
    try {
      await usersAPI.update(id, data);
      setEditUser(null);
      load();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed');
    } finally {
      setBusy('');
    }
  }

  async function handleRoleChange(id, newRole) {
    setBusy(`role_${id}`); setError('');
    try {
      await usersAPI.update(id, { role: newRole });
      load();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed');
    } finally {
      setBusy('');
    }
  }

  async function handleStatusToggle(id, currentStatus) {
    const newStatus = currentStatus === 'active' ? 'inactive' : 'active';
    setBusy(`status_${id}`); setError('');
    try {
      await usersAPI.update(id, { status: newStatus });
      load();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed');
    } finally {
      setBusy('');
    }
  }

  const boardCount   = users.filter(u => u.role === 'board').length;
  const financeCount = users.filter(u => u.role === 'finance').length;
  const adminCount   = users.filter(u => u.role === 'admin').length;

  return (
    <div className="space-y-5">

      {/* Header */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-xl font-bold text-slate-800">Staff & Approvers</h1>
          <p className="text-sm text-slate-400 mt-0.5">
            Manage staff accounts and configure who approves claims
          </p>
        </div>
        <button
          onClick={() => { setShowAdd(true); setTempPassword(''); setError(''); }}
          className="rounded-xl bg-orange-600 hover:bg-orange-500 text-white px-4 py-2 text-sm font-semibold transition-colors">
          + Add Staff Member
        </button>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-3 gap-3">
        <SummaryCard label="Board Approvers" count={boardCount} color="border-violet-300 bg-violet-50" tc="text-violet-700"
          onClick={() => setActiveTab('board')} />
        <SummaryCard label="Finance"         count={financeCount} color="border-blue-300 bg-blue-50"     tc="text-blue-700"
          onClick={() => setActiveTab('finance')} />
        <SummaryCard label="Admins"          count={adminCount}   color="border-purple-300 bg-purple-50" tc="text-purple-700"
          onClick={() => setActiveTab('admin')} />
      </div>

      {/* Board info banner */}
      <div className="rounded-2xl border border-violet-200 bg-violet-50 px-4 py-3 flex gap-3 items-start">
        <div className="mt-0.5 shrink-0 h-5 w-5 rounded-full bg-violet-600 flex items-center justify-center">
          <span className="text-white text-[10px] font-bold">i</span>
        </div>
        <div>
          <p className="text-sm font-semibold text-violet-800">Board Members are Claims Approvers</p>
          <p className="text-xs text-violet-600 mt-0.5">
            Users with the <strong>Board Member</strong> role receive approval requests when a claim is sent to the board.
            Majority vote triggers automatic approval. Add at least 3 board members for a proper quorum.
          </p>
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div>
      )}

      {/* Tabs */}
      <div className="flex gap-1 rounded-xl bg-slate-100 p-1 w-fit overflow-x-auto">
        {TABS.map(t => (
          <button key={t.key} onClick={() => setActiveTab(t.key)}
            className={`shrink-0 rounded-lg px-3 py-1.5 text-xs font-medium transition-colors ${
              activeTab === t.key ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'
            }`}>
            {t.label}
          </button>
        ))}
      </div>

      {/* Table */}
      <div className="rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-16 text-slate-400 text-sm">Loading…</div>
        ) : users.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 gap-2">
            <p className="text-slate-500 font-medium text-sm">No staff found</p>
            <p className="text-xs text-slate-400">Add a staff member using the button above</p>
          </div>
        ) : (
          <>
            {/* Desktop */}
            <div className="hidden sm:block overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="border-b border-slate-100 bg-slate-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-400">Name</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-400">Email</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-400">Role</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-400">Status</th>
                    <th className="px-4 py-3 w-48" />
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {users.map(u => (
                    <tr key={u.id} className="hover:bg-slate-50 transition-colors">
                      <td className="px-4 py-3 font-medium text-slate-900">{u.full_name}</td>
                      <td className="px-4 py-3 text-xs text-slate-500">{u.email}</td>
                      <td className="px-4 py-3">
                        <select
                          value={u.role}
                          disabled={busy === `role_${u.id}`}
                          onChange={e => handleRoleChange(u.id, e.target.value)}
                          className="rounded-lg border border-slate-200 px-2 py-1 text-xs font-medium focus:outline-none focus:ring-2 focus:ring-orange-500 disabled:opacity-50">
                          <option value="admin">Admin</option>
                          <option value="finance">Finance</option>
                          <option value="board">Board Member</option>
                          <option value="member">Member (remove staff)</option>
                        </select>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex flex-wrap items-center gap-1.5">
                          <button
                            disabled={busy === `status_${u.id}`}
                            onClick={() => handleStatusToggle(u.id, u.status)}
                            className={`rounded-full px-2.5 py-0.5 text-xs font-semibold transition-colors disabled:opacity-50 ${
                              u.status === 'active'
                                ? 'bg-emerald-100 text-emerald-700 hover:bg-emerald-200'
                                : 'bg-red-100 text-red-700 hover:bg-red-200'
                            }`}>
                            {u.status === 'active' ? 'Active' : 'Inactive'}
                          </button>
                          {u.is_locked && (
                            <span className="rounded-full bg-red-600 px-2.5 py-0.5 text-xs font-semibold text-white">🔒 Locked</span>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <div className="flex items-center justify-end gap-2">
                          <button
                            disabled={!!busy}
                            onClick={() => { setEditUser(u); setError(''); }}
                            className="rounded-lg border border-slate-200 bg-white hover:bg-slate-50 px-2.5 py-1.5 text-xs font-medium text-slate-600 transition-colors disabled:opacity-50">
                            Edit
                          </button>
                          <button
                            disabled={!!busy || !isAdmin}
                            onClick={() => navigate(`/staff/${u.id}/permissions`)}
                            className="rounded-lg border border-slate-200 bg-white hover:bg-slate-50 px-2.5 py-1.5 text-xs font-medium text-slate-600 transition-colors disabled:opacity-50">
                            Permissions
                          </button>
                          {u.is_locked && (
                            <button
                              disabled={!!busy}
                              onClick={() => handleUnlock(u.id)}
                              className="rounded-lg border border-red-200 bg-white hover:bg-red-50 px-2.5 py-1.5 text-xs font-medium text-red-600 transition-colors disabled:opacity-50">
                              {busy === `unlock_${u.id}` ? 'Unlocking…' : 'Unlock'}
                            </button>
                          )}
                          <button
                            disabled={!!busy}
                            onClick={() => handleResetPassword(u.id)}
                            className="rounded-lg border border-slate-200 bg-white hover:bg-slate-50 px-2.5 py-1.5 text-xs font-medium text-slate-600 transition-colors disabled:opacity-50">
                            Reset Password
                          </button>
                          <button
                            disabled={!!busy}
                            onClick={() => handleDelete(u.id, u.full_name)}
                            className="rounded-lg border border-red-200 bg-white hover:bg-red-50 px-2.5 py-1.5 text-xs font-medium text-red-600 transition-colors disabled:opacity-50">
                            Deactivate
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Mobile */}
            <div className="sm:hidden divide-y divide-slate-100">
              {users.map(u => (
                <div key={u.id} className="p-4 space-y-3">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <p className="font-semibold text-slate-900 text-sm">{u.full_name}</p>
                      <p className="text-xs text-slate-400 mt-0.5">{u.email}</p>
                    </div>
                    <RoleBadge role={u.role} />
                  </div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <select
                      value={u.role}
                      disabled={busy === `role_${u.id}`}
                      onChange={e => handleRoleChange(u.id, e.target.value)}
                      className="rounded-lg border border-slate-200 px-2 py-1.5 text-xs font-medium focus:outline-none focus:ring-2 focus:ring-orange-500 disabled:opacity-50 flex-1">
                      <option value="admin">Admin</option>
                      <option value="finance">Finance</option>
                      <option value="board">Board Member</option>
                      <option value="member">Member (remove staff)</option>
                    </select>
                    <button
                      disabled={busy === `status_${u.id}`}
                      onClick={() => handleStatusToggle(u.id, u.status)}
                      className={`rounded-full px-2.5 py-1 text-xs font-semibold disabled:opacity-50 ${
                        u.status === 'active' ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'
                      }`}>
                      {u.status}
                    </button>
                    {u.is_locked && (
                      <span className="rounded-full bg-red-600 px-2.5 py-1 text-xs font-semibold text-white">🔒 Locked</span>
                    )}
                  </div>
                  <div className="flex gap-2">
                    <button
                      disabled={!!busy}
                      onClick={() => { setEditUser(u); setError(''); }}
                      className="flex-1 rounded-lg border border-slate-200 py-2 text-xs font-medium text-slate-600 hover:bg-slate-50 disabled:opacity-50">
                      Edit
                    </button>
                    <button
                      disabled={!!busy || !isAdmin}
                      onClick={() => navigate(`/staff/${u.id}/permissions`)}
                      className="flex-1 rounded-lg border border-slate-200 py-2 text-xs font-medium text-slate-600 hover:bg-slate-50 disabled:opacity-50">
                      Permissions
                    </button>
                    <button
                      disabled={!!busy}
                      onClick={() => handleResetPassword(u.id)}
                      className="flex-1 rounded-lg border border-slate-200 py-2 text-xs font-medium text-slate-600 hover:bg-slate-50 disabled:opacity-50">
                      Reset Password
                    </button>
                  </div>
                  <div className="flex gap-2">
                    {u.is_locked && (
                      <button
                        disabled={!!busy}
                        onClick={() => handleUnlock(u.id)}
                        className="flex-1 rounded-lg border border-red-200 py-2 text-xs font-medium text-red-600 hover:bg-red-50 disabled:opacity-50">
                        {busy === `unlock_${u.id}` ? 'Unlocking…' : 'Unlock Account'}
                      </button>
                    )}
                    <button
                      disabled={!!busy}
                      onClick={() => handleDelete(u.id, u.full_name)}
                      className="flex-1 rounded-lg border border-red-200 py-2 text-xs font-medium text-red-600 hover:bg-red-50 disabled:opacity-50">
                      Deactivate
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>

      {/* Add Staff Modal */}
      {showAdd && (
        <AddStaffModal
          onClose={() => { setShowAdd(false); setTempPassword(''); }}
          onCreated={(res) => {
            setTempPassword(res.temp_password || '');
            if (!res.temp_password) setShowAdd(false);
            load();
          }}
        />
      )}

      {/* Edit Staff Modal */}
      {editUser && (
        <EditStaffModal
          user={editUser}
          busy={busy === `edit_${editUser.id}`}
          error={error}
          onClose={() => { setEditUser(null); setError(''); }}
          onSave={(data) => handleEditSave(editUser.id, data)}
        />
      )}

      {/* Temp password display */}
      {tempPassword && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-slate-900/40 backdrop-blur-sm" />
          <div className="relative w-full max-w-sm bg-white rounded-2xl shadow-2xl p-6 text-center">
            <div className="h-12 w-12 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-4">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="h-6 w-6 text-emerald-600">
                <path strokeLinecap="round" strokeLinejoin="round" d="m5 13 4 4L19 7" />
              </svg>
            </div>
            <h3 className="text-base font-bold text-slate-800 mb-1">Account Created</h3>
            <p className="text-xs text-slate-500 mb-4">Share this temporary password with the user. They should change it after first login.</p>
            <div className="rounded-xl border-2 border-dashed border-slate-300 bg-slate-50 px-4 py-3 mb-4">
              <p className="text-xs text-slate-400 mb-1 uppercase tracking-widest">Temporary Password</p>
              <p className="text-xl font-mono font-bold text-slate-900 tracking-widest">{tempPassword}</p>
            </div>
            <button
              onClick={() => { setTempPassword(''); setShowAdd(false); }}
              className="w-full rounded-xl bg-orange-600 hover:bg-orange-500 text-white py-2.5 text-sm font-semibold transition-colors">
              Done
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function SummaryCard({ label, count, color, tc, onClick }) {
  return (
    <button
      onClick={onClick}
      className={`rounded-2xl border-2 ${color} p-4 text-left transition-all hover:shadow-md w-full`}>
      <p className={`text-2xl font-bold ${tc}`}>{count}</p>
      <p className="text-xs font-medium text-slate-500 mt-0.5">{label}</p>
    </button>
  );
}

function AddStaffModal({ onClose, onCreated }) {
  const [form, setForm] = useState({ full_name: '', email: '', role: 'board' });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  async function handleSubmit(e) {
    e.preventDefault();
    setBusy(true); setError('');
    try {
      const res = await usersAPI.create(form);
      onCreated(res);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to create account');
      setBusy(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-slate-900/40 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-sm bg-white rounded-2xl shadow-2xl p-6">
        <h3 className="text-base font-bold text-slate-800 mb-4">Add Staff Member</h3>
        {error && (
          <div className="mb-3 rounded-xl border border-red-200 bg-red-50 px-3 py-2.5 text-xs text-red-700">{error}</div>
        )}
        <form onSubmit={handleSubmit} className="space-y-4">
          <Field label="Full Name *">
            <input required type="text" value={form.full_name}
              onChange={e => setForm(f => ({ ...f, full_name: e.target.value }))}
              placeholder="John Doe"
              className="w-full rounded-xl border border-slate-200 px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500" />
          </Field>
          <Field label="Email Address *">
            <input required type="email" value={form.email}
              onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
              placeholder="john@example.com"
              className="w-full rounded-xl border border-slate-200 px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500" />
          </Field>
          <Field label="Role *">
            <select value={form.role}
              onChange={e => setForm(f => ({ ...f, role: e.target.value }))}
              className="w-full rounded-xl border border-slate-200 px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500">
              <option value="board">Board Member (Claims Approver)</option>
              <option value="finance">Finance</option>
              <option value="admin">Admin</option>
            </select>
          </Field>
          <p className="text-[11px] text-slate-400 bg-slate-50 rounded-xl px-3 py-2 border border-slate-200">
            If the email belongs to an existing member, they will be promoted to the selected role without losing their member account.
            Otherwise, a new account is created with a temporary password.
          </p>
          <div className="flex gap-3 pt-1">
            <button type="button" onClick={onClose}
              className="flex-1 rounded-xl border border-slate-200 py-2.5 text-sm font-medium text-slate-700 hover:bg-slate-50">
              Cancel
            </button>
            <button type="submit" disabled={busy}
              className="flex-1 rounded-xl bg-orange-600 hover:bg-orange-500 disabled:opacity-60 text-white py-2.5 text-sm font-semibold">
              {busy ? 'Creating…' : 'Create Account'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function EditStaffModal({ user, busy, error, onClose, onSave }) {
  const [form, setForm] = useState({ full_name: user.full_name, email: user.email });

  function handleSubmit(e) {
    e.preventDefault();
    const data = {};
    if (form.full_name.trim() !== user.full_name) data.full_name = form.full_name.trim();
    if (form.email.trim().toLowerCase() !== user.email.toLowerCase()) data.email = form.email.trim();
    if (Object.keys(data).length === 0) { onClose(); return; }
    onSave(data);
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-slate-900/40 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-sm bg-white rounded-2xl shadow-2xl p-6">
        <h3 className="text-base font-bold text-slate-800 mb-4">Edit Staff Member</h3>
        {error && (
          <div className="mb-3 rounded-xl border border-red-200 bg-red-50 px-3 py-2.5 text-xs text-red-700">{error}</div>
        )}
        <form onSubmit={handleSubmit} className="space-y-4">
          <Field label="Full Name *">
            <input required type="text" value={form.full_name}
              onChange={e => setForm(f => ({ ...f, full_name: e.target.value }))}
              className="w-full rounded-xl border border-slate-200 px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500" />
          </Field>
          <Field label="Email Address *">
            <input required type="email" value={form.email}
              onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
              className="w-full rounded-xl border border-slate-200 px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500" />
          </Field>
          <div className="flex gap-3 pt-1">
            <button type="button" onClick={onClose}
              className="flex-1 rounded-xl border border-slate-200 py-2.5 text-sm font-medium text-slate-700 hover:bg-slate-50">
              Cancel
            </button>
            <button type="submit" disabled={busy}
              className="flex-1 rounded-xl bg-orange-600 hover:bg-orange-500 disabled:opacity-60 text-white py-2.5 text-sm font-semibold">
              {busy ? 'Saving…' : 'Save Changes'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="block text-xs font-medium text-slate-700 mb-1.5">{label}</label>
      {children}
    </div>
  );
}
